import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Image as ImageIcon } from "lucide-react";

function normalizeUrl(u) {
  if (!u) return "";
  let s = String(u).trim();
  s = s.replace(/^<|>$/g, ""); // remove <...>
  if (s.startsWith("//")) s = "https:" + s;
  s = s.replace(/^"+|"+$/g, "").replace(/^'+|'+$/g, ""); // remove aspas soltas
  return s;
}

function isValidHttpUrl(u) {
  try {
    const url = new URL(u);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function repairTruncatedParens(u) {
  let s = normalizeUrl(u);
  if (!isValidHttpUrl(s)) return "";

  const open = (s.match(/\(/g) || []).length;
  const close = (s.match(/\)/g) || []).length;
  if (open > close) s += ")".repeat(open - close);

  return s;
}

function uniqUrls(urls) {
  const out = [];
  const seen = new Set();
  for (const u of urls) {
    const x = repairTruncatedParens(u);
    if (!x || seen.has(x)) continue;
    seen.add(x);
    out.push(x);
  }
  return out;
}

export default function NoticiaCardImage({ imageUrl, title, viewMode, slug }) {
  const [src, setSrc] = useState(() => repairTruncatedParens(imageUrl));
  const [status, setStatus] = useState("idle"); // idle | loadingAlt | noImage
  const failedRef = useRef(new Set());
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    failedRef.current = new Set();
    const first = repairTruncatedParens(imageUrl);
    setSrc(first);
    if (first) {
      setStatus("idle");
    } else if (slug) {
      setStatus("loadingAlt");
      (async () => {
        try {
          const res = await axios.get(`/news/${slug}`);
          if (!aliveRef.current) return;
          const n = res.data?.data;
          const candidates = uniqUrls(
            (Array.isArray(n?.images) ? n.images.map((x) => x?.url ?? x) : [])
          );
          for (const u of candidates) {
            if (u) {
              setSrc(u);
              setStatus("idle");
              return;
            }
          }
          setStatus("noImage");
        } catch {
          if (aliveRef.current) setStatus("noImage");
        }
      })();
    } else {
      setStatus("noImage");
    }
  }, [imageUrl, slug]);

  const boxClass =
    viewMode === "list"
      ? "w-full sm:w-48 h-48 sm:h-32 rounded-lg flex-shrink-0"
      : "w-full h-48 -mx-6 -mt-6 mb-4 rounded-t-xl";

  const placeholder = (
    <div className={`relative overflow-hidden bg-gray-100 ${boxClass}`}>
      <div className="absolute inset-0 flex items-center justify-center text-gray-400">
        <div className="flex items-center gap-2 text-sm">
          <ImageIcon size={18} />
          <span>{status === "loadingAlt" ? "Carregando imagem..." : "Sem imagem"}</span>
        </div>
      </div>
    </div>
  );

  const handleError = async (e) => {
    if (e?.currentTarget) e.currentTarget.style.display = "none";

    const current = src;
    if (current) failedRef.current.add(current);

    if (!slug || status === "loadingAlt") {
      setSrc(null);
      setStatus("noImage");
      return;
    }

    setStatus("loadingAlt");

    try {
      const res = await axios.get(`/news/${slug}`);
      if (!aliveRef.current) return;

      const n = res.data?.data;

      const candidates = uniqUrls([
        imageUrl,
        n?.imageUrl,
        ...(Array.isArray(n?.images) ? n.images.map((x) => x?.url ?? x) : []),
      ]);

      for (const u of candidates) {
        if (!u) continue;
        if (failedRef.current.has(u)) continue;

        failedRef.current.add(u);
        setSrc(u);
        setStatus("idle");
        return;
      }

      setSrc(null);
      setStatus("noImage");
    } catch (err) {
      console.error("Erro ao buscar imagens alternativas", err);
      setSrc(null);
      setStatus("noImage");
    }
  };

  if (!src) return placeholder;

  return (
    <div className={`relative overflow-hidden bg-white border-b border-gray-100 ${boxClass}`}>
      <img
        key={src}
        src={src}
        alt=""
        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-102 p-3"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={handleError}
        onLoad={() => setStatus("idle")}
      />
      {status === "loadingAlt" && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 text-gray-500 text-sm">
          <span className="inline-flex items-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
            Carregando...
          </span>
        </div>
      )}
    </div>
  );
}
